import { useState, useEffect, useRef } from 'react';
import { Image, FileIcon, Send, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export type SendMode = 'media' | 'document';

interface PasteMediaModalProps {
  file: File | null;
  open: boolean;
  onClose: () => void;
  onSend: (file: File, caption: string, mode: SendMode) => void;
  isLoading?: boolean;
}

/**
 * Модальное окно для выбора способа отправки вставленного файла
 * Как в Telegram: можно отправить как фото/видео (со сжатием) или как документ
 */
export function PasteMediaModal({
  file,
  open,
  onClose,
  onSend,
  isLoading = false,
}: PasteMediaModalProps) {
  const [caption, setCaption] = useState('');
  const [sendMode, setSendMode] = useState<SendMode>('media');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  // Генерируем превью для изображений
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }

    setPreviewUrl(null);
  }, [file]);

  // Сброс состояния при открытии/закрытии
  useEffect(() => {
    if (open) {
      setCaption('');
      setSendMode('media');
      // Фокус на поле ввода подписи
      setTimeout(() => captionRef.current?.focus(), 100);
    }
  }, [open]);

  // Автоматически определяем режим отправки по типу файла
  useEffect(() => {
    if (!file) {return;}

    const isMedia = file.type.startsWith('image/') || file.type.startsWith('video/');
    setSendMode(isMedia ? 'media' : 'document');
  }, [file]);

  const handleSend = () => {
    if (!file || isLoading) {return;}
    onSend(file, caption.trim(), sendMode);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!file) {return null;}

  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const canBeMedia = isImage || isVideo;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {sendMode === 'media' ? (
              <>
                <Image className="h-5 w-5" />
                Отправить {isImage ? 'изображение' : (isVideo ? 'видео' : 'медиа')}
              </>
            ) : (
              <>
                <FileIcon className="h-5 w-5" />
                Отправить файл
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Превью файла */}
          <div className="relative rounded-lg overflow-hidden bg-muted flex items-center justify-center min-h-[200px]">
            {isImage && previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-[300px] max-w-full object-contain"
              />
            ) : (isVideo && previewUrl ? (
              <video
                src={previewUrl}
                className="max-h-[300px] max-w-full object-contain"
                controls={false}
                muted
                autoPlay
                loop
              />
            ) : (
              <div className="flex flex-col items-center gap-2 py-8">
                <FileIcon className="h-16 w-16 text-muted-foreground" />
                <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                  {file.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)}
                </span>
              </div>
            ))}
          </div>

          {/* Выбор режима отправки */}
          {canBeMedia && (
            <div className="flex gap-2">
              <Button
                variant={sendMode === 'media' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() => setSendMode('media')}
              >
                <Image className="h-4 w-4 mr-2" />
                {isImage ? 'Фото' : 'Видео'}
              </Button>
              <Button
                variant={sendMode === 'document' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() => setSendMode('document')}
              >
                <FileIcon className="h-4 w-4 mr-2" />
                Файл
              </Button>
            </div>
          )}

          {/* Подпись */}
          <div className="space-y-2">
            <Textarea
              ref={captionRef}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Добавить подпись..."
              className="min-h-[60px] max-h-[120px] resize-none"
              disabled={isLoading}
            />
          </div>

          {/* Информация о режиме */}
          <p className="text-xs text-muted-foreground">
            {sendMode === 'media' ? (
              isImage ? 'Изображение будет сжато для быстрой загрузки' :
              'Видео будет оптимизировано для просмотра'
            ) : (
              'Файл будет отправлен без сжатия'
            )}
          </p>

          {/* Кнопки */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Отмена
            </Button>
            <Button onClick={handleSend} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Отправить
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} Б`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} КБ`;}
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}
